import type { PersonRequestSchemaType } from "@/components/Forms/PersonForm.tsx";
import { useFetchPeopleForAutocomplete } from "@/queries/useFetchPeopleForAutocomplete.ts";
import { Typography } from "@mui/material";
import { RhfAutocomplete } from "mui-rhf-integration";
import type React from "react";
import { useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

type Option = {
    id: string | null;
    name: string | null;
};

type Props = {
    form: UseFormReturn<PersonRequestSchemaType>;
};

const PersonReferredByIndividualAutocomplete = ({ form }: Props) => {
    const [searchString, setSearchString] = useState("");
    const formValue = form.watch("idReferredBy");

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchPeopleForAutocomplete({
        searchString: searchString,
        fieldIdValue: formValue ?? null,
    });

    const options: Option[] = useMemo(() => {
        if (data) {
            return data.map((person) => ({
                id: person.idUUID,
                name: person.fullName ?? "",
            }));
        }
        return [];
    }, [data]);

    const handleInputChange = async (_event: React.SyntheticEvent, newInputValue: string) => {
        if (newInputValue.length >= 2) {
            setSearchString(newInputValue);
        }
    };

    return (
        <>
            {isError && <Typography variant={"h5"}>Error loading people</Typography>}
            <RhfAutocomplete
                control={form.control}
                name="idReferredBy"
                options={options}
                loading={isLoading || isRefetching}
                onInputChange={handleInputChange}
                textFieldProps={{
                    label: "Referred By",
                }}
                // display the name attribute as the dropdown value
                getOptionLabel={(option: Option | string) => {
                    if (typeof option === "string") {
                        return option;
                    }

                    return option.name || "";
                }}
                getOptionKey={(option: Option | string) => {
                    if (typeof option === "string") {
                        return option;
                    }

                    return option.id || "";
                }}
                optionToValue={(option: Option | string) => {
                    if (typeof option === "string") {
                        return option;
                    }

                    return option.id || "";
                }}
                valueToOption={(value) => {
                    return options.find((option) => option.id === value);
                }}
                sx={{
                    "& .MuiAutocomplete-inputRoot": {
                        padding: "4px!important",
                    },
                    "& .MuiAutocomplete-input": {
                        paddingTop: "4px!important",
                        paddingBottom: "4px!important",
                    },
                }}
            />
        </>
    );
};

export default PersonReferredByIndividualAutocomplete;
